<template>
	<div class="hdl-reset-password hdl-auth-container">
		<div v-if="screen === 'send'">
			<div class="text-center text-3xl mb-5">
				<font-awesome-icon :icon="['fal', 'key']" fixed-width />
			</div>
			<div class="hdl-heading-secondary text-center mb-5">
				Reset your password
			</div>
			<div class="mb-5">
				<div class="mb-1 text-lg font-semibold">Email</div>
				<hdl-textbox
					v-model="email"
					hdl-style="auth"
					class="w-full"
					focus-on-mount
				/>
			</div>
			<div class="mb-8">
				<hdl-button
					@click.prevent="sendResetEmail"
					label="Send password reset email"
					hdl-style="primary"
					:is-loading="sendingEmail"
					size="lg"
					class="w-full"
				/>
			</div>
		</div>
		<div v-else-if="screen === 'sent'">
			<div class="text-center text-3xl mb-2">
				<font-awesome-icon
					:icon="['fal', 'envelope-circle-check']"
					bounce
				/>
			</div>
			<div class="hdl-heading-secondary text-center mb-5">
				Password reset email sent
			</div>
			<div class="text-center mb-8 text-lg">
				We've sent an email to {{ email }} to reset your password. If you're having trouble finding it check your spam folder!
			</div>
		</div>
		<div v-else-if="screen === 'change'">
			<div class="text-center text-3xl mb-5">
				<font-awesome-icon :icon="['fas', 'key']" fixed-width />
			</div>
			<div class="hdl-heading-secondary text-center mb-5">
				Reset your password
			</div>
			<div class="mb-3">
				<div class="mb-1 text-lg font-semibold">Password</div>
				<hdl-textbox
					v-model="password"
					hdl-style="auth"
					class="w-full"
					password
					focus-on-mount
				/>
			</div>
			<div class="mb-5 text-lg font-semibold">
				<div class="mb-1">Password again</div>
				<hdl-textbox
					v-model="passwordAgain"
					hdl-style="auth"
					password
					class="w-full"
				/>
			</div>
			<div class="mb-8">
				<hdl-button
					@click.prevent="resetPassword"
					label="Reset your password"
					hdl-style="primary"
					size="lg"
					class="w-full"
					:disabled="!resetPasswordValid"
					:is-loading="resettingPassword"
				/>
			</div>
		</div>
		<div class="mb-8 hdl-footnote-text text-center">
			<div class="mb-2">
				Remember your password?
				<a
					href="#"
					@click.prevent="$router.push('/account/login/')"
				>Back to sign in</a>.
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faKey, faEnvelopeCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { fetcher, FetchResult } from '@/common/fetcher';
import { useToast } from 'vue-toastification';

library.add(faKey, faEnvelopeCircleCheck);

declare type ResetScreen = 'send' | 'change' | 'sent';

@Component
export default class ResetPasswordPage extends Vue {
	email: string = "";
	token: string | null = null;

	password: string = "";
	passwordAgain: string = "";

	screen: ResetScreen = 'send';

	sendingEmail: boolean = false;

	resettingPassword: boolean = false;

	created(): void {
		if (this.$route.query.t) {
			this.token = this.$route.query.t as string;
			this.screen = 'change';
		}
	}

	resetPassword(): void {
		if (!this.resetPasswordValid) {
			return;
		}

		this.resettingPassword = true;
		fetcher.Post(
			`/account/password/reset/?t=${this.token}`,
			{
				password: this.password
			}
		).then(
			(result: FetchResult) => {
				const toast = useToast();
				if (result.response.ok && result.json.success) {
					window.location.replace('/');
				} else if (!result.json.success) {
					if (result.json.message) {
						toast.error(result.json.message);
					} else {
						toast.error('Something went wrong, try again soon.');
					}
				}
			}
		);
	}

	sendResetEmail(): void {
		this.sendingEmail = true;
		fetcher.Put(
			'/account/password/reset/',
			{
				email: this.email
			}
		).then(
			(result: FetchResult) => {
				const toast = useToast();
				if (result.response.ok && result.json.success) {
					this.screen = 'sent';
				} else if (!result.json.success) {
					if (result.json.message) {
						toast.error(result.json.message);
					} else {
						toast.error('Something went wrong, please try again soon.');
					}
				}

				this.sendingEmail = false;
			}
		);
	}

	get resetPasswordValid(): boolean {
		return (
			this.password.trim().length >= 8
			&& this.passwordAgain.trim() === this.password.trim()
		);
	}
}
</script>

<style lang="scss">
</style>
